import type { PlaylistItem, ScreenDefaultItem, ScreenInfo } from "../api/types";
import { getBridge } from "../bridge";

// Last-good schedule snapshot, persisted on every sync that returns a schedule (not a 304). A reload
// during a backend outage rehydrates from this (credentials and media bytes
// already survive reloads) instead of blanking on "Waiting for content…"
// until the next successful sync.

const SCHEDULE_CACHE_KEY = "gocityscreens.schedule.cache";

export interface ScheduleSnapshot {
  screen: ScreenInfo;
  playlist: PlaylistItem[];
  screenDefault: ScreenDefaultItem | null;
  etag: string;
  // Clock offset, not a timestamp — stays as accurate across a reload as it
  // was when captured (the boot sync is forced anyway and refreshes it).
  serverOffsetMs: number;
}

export async function saveScheduleCache(snapshot: ScheduleSnapshot): Promise<void> {
  try {
    await getBridge().secureStore.set(SCHEDULE_CACHE_KEY, JSON.stringify(snapshot));
  } catch (err) {
    console.warn("[scheduleCache] persist failed", err);
  }
}

export async function loadScheduleCache(): Promise<ScheduleSnapshot | null> {
  try {
    const raw = await getBridge().secureStore.get(SCHEDULE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ScheduleSnapshot;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !parsed.screen ||
      !Array.isArray(parsed.playlist) ||
      typeof parsed.etag !== "string"
    ) {
      return null;
    }
    return {
      ...parsed,
      // Snapshots persisted before the screen-default tier lack the field.
      screenDefault: parsed.screenDefault ?? null,
      serverOffsetMs: typeof parsed.serverOffsetMs === "number" ? parsed.serverOffsetMs : 0,
    };
  } catch {
    return null;
  }
}

export async function clearScheduleCache(): Promise<void> {
  try {
    await getBridge().secureStore.remove(SCHEDULE_CACHE_KEY);
  } catch {
    /* best effort */
  }
}
